import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import type { Bulletin, BulletinList, PostCategory } from '@up2d8/contracts';
import { SectionHead, bulletinPeriodLine, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * De strook "Mededelingen" bovenaan het beginscherm (replica-spec §7).
 *
 * Twee indelingen uit dezelfde gegevens, want de mockup heeft ze allebei: op een bureauscherm een
 * raster van vier kaarten, op een telefoon een strook die horizontaal schuift. Eén component, want
 * het is één soort inhoud — en twee componenten zouden betekenen dat een wijziging er maar op één
 * plek in belandt.
 *
 * Tot UI-6 stond hier een afgeleide: de berichten die nog bevestigd moesten worden, met één chip.
 * Dat was het beste wat er zonder gegevens te maken viel. Nu heeft een bericht een categorie en een
 * periode, en toont de strook wat ze hoort te tonen.
 */

/** Het typelabel per soort, met exact de kleuren uit §7 van de spec. */
const TYPE_STYLE: Record<Exclude<PostCategory, 'geen'>, string> = {
  // `#FEF3E4` en `#B85B1F` staan niet in de tokenset: het zijn een tint en een donkere variant die
  // alleen in dit label voorkomen. Overgenomen uit de referentie-CSS, zie deviations.md.
  werken: 'bg-[#FEF3E4] text-[color:var(--brand-orange-strong)]',
  veiligheid: 'bg-brand-primary-tint text-brand-primary-dark',
  menu: 'bg-success-tint text-[#14532D]',
  hr: 'bg-brand-dark-blue-tint text-brand-dark-blue',
};

/**
 * De rand links per soort (FBK-3): de testers vonden dat een mededeling niet opviel tussen de
 * berichten. Een gekleurde rand en een lichte tint maken van de kaart een aankondiging, zonder het
 * raster van de spec te breken. Zonder soort: de primaire kleur.
 */
const TYPE_ACCENT: Record<PostCategory, string> = {
  werken: 'border-l-[color:var(--brand-orange-strong)] bg-[#FFFBF5]',
  veiligheid: 'border-l-brand-primary bg-brand-primary-tint',
  menu: 'border-l-success bg-success-tint',
  hr: 'border-l-brand-dark-blue bg-brand-dark-blue-tint',
  geen: 'border-l-brand-primary bg-surface',
};

/** De gevulde iconen van de vier typelabels (spec §11: deze zijn `fill`, geen `stroke`). */
const TYPE_ICON: Record<Exclude<PostCategory, 'geen'>, ReactNode> = {
  werken: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2L1 21h22L12 2zm0 15h-1v-2h1v2zm0-4h-1V9h1v4z" />
    </svg>
  ),
  menu: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 2v8a2 2 0 0 0 2 2v10h2V12a2 2 0 0 0 2-2V2h-1.5v6h-1V2h-1v6h-1V2H7zm10 0c-1.1 0-2 1.8-2 4v6h1.5v10H18V2h-1z" />
    </svg>
  ),
  veiligheid: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  ),
  hr: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5 0-9 2.5-9 5v3h18v-3c0-2.5-4-5-9-5z" />
    </svg>
  ),
};

/** De klok en de kalender van de metaregel; lijniconen, zoals de rest. */
const META_ICON = {
  calendar: (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  clock: (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
} as const;

function BulletinCard({ item, onDismiss }: { item: Bulletin; onDismiss: () => void }) {
  const { t } = useTranslation();
  const soort = item.category === 'geen' ? null : item.category;

  // De dag van vandaag, in de taal van het toestel. De server filtert al op de periode; dit is
  // alleen de regel die eronder staat. De lokale datum en niet `toISOString()`: die is UTC, en
  // tussen middernacht en twee uur 's nachts zei "vandaag" dan nog gisteren.
  const nu = new Date();
  const vandaag = `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, '0')}-${String(nu.getDate()).padStart(2, '0')}`;
  const periode = bulletinPeriodLine(item, vandaag);

  return (
    <div
      data-testid="bulletin"
      data-category={item.category}
      className={cn(
        'relative flex min-h-[130px] w-64 shrink-0 snap-start flex-col gap-2 overflow-hidden rounded-lg border border-l-4 border-border-light p-4 shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg desk:w-auto',
        TYPE_ACCENT[item.category],
      )}
    >
      <div className="flex items-center justify-between gap-2.5">
        {soort ? (
          <span
            data-testid="bulletin-type"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 font-heading text-micro font-semibold uppercase tracking-[0.05em]',
              TYPE_STYLE[soort],
            )}
          >
            {TYPE_ICON[soort]}
            {t(`bulletins.category.${soort}`)}
          </span>
        ) : (
          <span />
        )}

        {/*
          Wegklikken verbergt de mededeling alleen in deze strook. Het bericht blijft in de tijdlijn
          staan, met zijn bevestigingsplicht — anders zou dit kruisje een manier zijn om een
          veiligheidsinstructie te laten verdwijnen.
        */}
        <button
          type="button"
          data-testid="bulletin-dismiss"
          aria-label={t('bulletins.dismiss')}
          onClick={onDismiss}
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-pill text-text-muted transition-colors hover:bg-background hover:text-text-primary"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <Link
        to="/app/posts/$postId"
        params={{ postId: item.postId }}
        className="flex flex-1 flex-col gap-2 focus-visible:outline-none"
      >
        <span className="font-heading text-content font-bold leading-[1.35] text-text-primary">
          {item.title}
        </span>
        <span className="line-clamp-2 text-small leading-[1.45] text-text-secondary">
          {item.excerpt}
        </span>
        {periode.text ? (
          <span className="mt-auto flex items-center gap-1.5 font-heading text-caption font-medium text-text-muted">
            {META_ICON[periode.icon]}
            {periode.text}
          </span>
        ) : null}
      </Link>
    </div>
  );
}

export function AppBulletins() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const bulletins = useQuery({
    queryKey: ['bulletins'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/feed/bulletins', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as BulletinList).items;
    },
  });

  const dismiss = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await api.POST('/api/v1/feed/bulletins/{postId}/dismiss', {
        params: { path: { postId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
    },
    // Zonder melding leek de knop gewoon niets te doen.
    onError: () => toast.show({ tone: 'danger', title: t('errors.generic') }),
  });

  const items = bulletins.data ?? [];

  // Niets te melden betekent geen strook: geen kop, geen lege ruimte. Een blok dat er altijd staat,
  // wordt niet meer gelezen.
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="bulletins-heading" data-testid="bulletin-row">
      {/*
        Geen link "Alles": er bestaat geen aparte lijst van mededelingen, en de strook toont ze al
        allemaal. De link wees naar `/app`, de pagina waar je al stond (testrapport 28 okt.).
      */}
      <SectionHead id="bulletins-heading" title={t('bulletins.title')} />

      {/*
        Op een telefoon schuift de strook horizontaal met `scroll-snap`; vanaf een bureauscherm is ze
        een raster van vier (twee onder 1400 px, zoals §12 voorschrijft). De negatieve marge laat de
        strook tot de schermrand lopen terwijl de kaarten binnen de paginamarge beginnen — zo is aan
        de rand te zien dat er meer volgt.
      */}
      <div className="-mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-4 pb-1 desk:mx-0 desk:grid desk:grid-cols-2 desk:gap-3 desk:overflow-visible desk:px-0 full:grid-cols-4">
        {items.map((item) => (
          <BulletinCard
            key={item.postId}
            item={item}
            onDismiss={() => dismiss.mutate(item.postId)}
          />
        ))}
      </div>
    </section>
  );
}
